import Feature from "components/feature";
import enGb from "date-fns/locale/en-GB";
import enUS from "date-fns/locale/en-US";
import es from "date-fns/locale/es";
import ptBR from "date-fns/locale/pt-BR";
import { Navbar } from "features/navbar";
import SurveyNps from "features/survey-nps";
import { useNewNpsScript } from "hooks/useNewNpsScript";
import locales from "locales";
import momentEnGb from "locales/moment/moment-en-GB";
import momentEnUs from "locales/moment/moment-en-US";
import momentEsEs from "locales/moment/moment-es-ES";
import momentPtBr from "locales/moment/moment-pt-BR";
import { MuiThemeProvider } from "material-ui/styles";
import moment from "moment";
import { useAuth } from "oidc-react";
import ErrorPage from "pages/404";
import PropTypes from "prop-types";
import { getUser } from "providers/authService";
import { loadMonitoringApplications } from "providers/monitoring";
import { createContext, useContext, useEffect, useState } from "react";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import intl from "react-intl-universal";
import { initGeneralSettings } from "services/general-settings/general-settings";
import { getShowNPS } from "utils/config-environment";
import { getDocumentTypes } from "utils/document-types";
import { storageDailyDeliveryFeatureFlagAsync } from "utils/feature-flags";
import Session from "utils/user-storage";
import { getDefaultLocale, getLocaleForLanguageSheet } from "utils/userLanguage";
import { verifyFilterUser } from "../config/user/user.service";
import { LoadColumsTable } from "../config/user/utils/colums-tables";
import { loadOrderCancellationJustifications } from "../pages/master-data/configurations/general-settings/general-settings.service";
import { perfilPcpUnidadeLogado } from "../services/permissions/getPermissions";
import { GlobalProvider } from "./globalContext";
import { useThemeContext } from "./themeContext";
import { ToastProvider } from "./toastContext";

/**
 * Contexto criado para encapsular lógica de obtenção de dados do
 * usuário pós SSO para acesso a aplicação.
 */
const AppContextKc = createContext();
const AppProviderKc = ({ children }) => {
  const [initDone, setInitDone] = useState(false);
  const auth = useAuth();
  const [noAccount, setNoAccount] = useState(false);
  const { theme } = useThemeContext();
  useNewNpsScript(auth.userData?.id_token);

  const loadLocales = async (user) => {
    const language = getLocaleForLanguageSheet(user.LocaleCode);
    await intl.init({
      currentLocale: language,
      locales,
    });

    setUpLocalizationLibsSettings(user.LocaleCode);
  };

  const setUpLocalizationLibsSettings = (language) => {
    registerLocale("pt-BR", ptBR);
    registerLocale("es-ES", es);
    registerLocale("en-US", enUS);
    registerLocale("en-Gb", enGb);

    const _defaultLocale = getDefaultLocale(language);
    setDefaultLocale(_defaultLocale);

    switch (language) {
      case "pt-BR":
        moment.updateLocale("pt-br", momentPtBr);
        break;
      case "es-ES":
      case "es-BO":
      case "es-MX":
        moment.updateLocale("es", momentEsEs);
        moment.locale("es");
        break;
      case "en-GB":
        moment.updateLocale("en-gb", momentEnGb);
        break;
      case "en-US":
      default:
        moment.updateLocale("en", momentEnUs);
        break;
    }
  };

  const loadDocumentTypes = async () => {
    const documentTypes = await getDocumentTypes();
    localStorage.setItem("DocumentTypes", JSON.stringify(documentTypes));
  };

  const loadAppAndUserConfig = async () => {
    window.document.title = "Ontime MRP";
    loadMonitoringApplications();

    const activeUser = await getUser();
    await loadLocales(activeUser);

    if (!auth.isLoading && auth.userManager && !activeUser) {
      setNoAccount(true);
      return;
    }

    if (activeUser) {
      await loadDocumentTypes();
      await initGeneralSettings();
      await storageDailyDeliveryFeatureFlagAsync();
      setInitDone(true);
    }

    LoadColumsTable();

    try {
      if (perfilPcpUnidadeLogado()) {
        localStorage.setItem("filterMyItemsOnly", true);
      } else {
        const myItemsOnly = localStorage.getItem("filterMyItemsOnly");

        if (myItemsOnly === null) {
          const filterMyItemsOnly = await verifyFilterUser(activeUser.Id);
          localStorage.setItem("filterMyItemsOnly", filterMyItemsOnly.data);
        }
      }

      const orderCancellationJustifications = await loadOrderCancellationJustifications();

      const enumerators = {
        OrderCancellationJustifications: orderCancellationJustifications.data,
      };

      localStorage.setItem("enumerators", JSON.stringify(enumerators));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!initDone) {
      loadAppAndUserConfig();
    }
  });

  if (noAccount) {
    return (
      <MuiThemeProvider>
        <ErrorPage />
      </MuiThemeProvider>
    );
  }

  return (
    initDone &&
    Session.get() && (
      <GlobalProvider>
        <ToastProvider>
          <AppContextKc.Provider>
            <main className={`container-main ${theme}`}>
              <Navbar />
              {children}
              <Feature validation={getShowNPS()}>
                <SurveyNps />
              </Feature>
            </main>
          </AppContextKc.Provider>
        </ToastProvider>
      </GlobalProvider>
    )
  );
};

AppProviderKc.propTypes = {
  children: PropTypes.any.isRequired,
};

export const useAppContextKc = () => useContext(AppContextKc);
export { AppProviderKc, AppContextKc };
